import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { GolfScoreCardModel } from 'src/app/_interfaces/golf/golf-score-card';
import { GolfEvent, GolfTournament } from 'src/app/_interfaces/golf/golf-tournament';
import { AnalyticsService, AppStringsService, FollowedPlayerService } from 'src/app/_services';
import { ScoreCardDetailsComponent } from './score-card-details/score-card-details.component';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { InfoDialogComponent } from 'src/app/_dialogs/info-dialog/info-dialog.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { formatLocalShortTimeFromCET } from 'src/app/_functions/datetimes';
import { Platform } from '@angular/cdk/platform';
import { MatInputModule } from '@angular/material/input';
import { Subscription, filter } from 'rxjs';
import { ScheduledEntryStateService } from 'src/app/tournament-section/tournament/scheduled-entry-state.service';
import { ImageComponent } from '../image/image.component';
import { LoadingIndicatorComponent } from '../loading-indicator/loading-indicator.component';
import { AppStringComponent } from '../app-string/app-string.component';
import { SponsorBannerComponent } from '../sponsor-banner/sponsor-banner.component';
import { SsrlocalstoreService } from 'src/app/_services/ssr-localstore.service';
import { GolfPlayer, Player } from 'src/app/_interfaces';
import { PlayerDialogComponent } from 'src/app/_dialogs/player-dialog/player-dialog.component';

export type GolfStatKey = 'ace' | 'double_eagle' | 'eagle' | 'birdie' | 'par' | 'bogey' | 'double_bogey' | 'triple_bogey' | 'other';

export type GolfStatsMap = Partial<Record<GolfStatKey, number | null>>;

export interface RoundScore {
    strokes: number | null;
    thru: string | null;
}

export interface LeaderRow {
    // Text shown in the “POS” column - may be CUT, WD, DQ, “-” or a number
    pos: string;
    // Pure numeric rank (1, 2, …).
    // Null for WD/DQ/rows without a numeric place. CUT rows keep placeNum when available
    placeNum: number | null;
    playerName: string;
    tot: string;
    thru: string;
    roundVals: (number | string)[];
    teeTime: string | null;
    startingHole: number | null;
    player_id: string;
    display_place?: string;
    source?: GolfScoreCardModel;
    rowIndex?: number;
    totalStrokes?: number | null;
    pairId?: string | number | null;
    sortOrder?: number | null;
    nonPlaying?: 'cut' | 'wd' | 'dq' | null;
}

type PlayerFilterValue = 'all' | 'live' | 'followed';
type PlayerFilterOption = { label: string; value: PlayerFilterValue };

@Component({
    selector: 'app-golf-scorecard',
    imports: [
        CommonModule,
        MatTableModule,
        MatIconModule,
        ScoreCardDetailsComponent,
        ImageComponent,
        MatButtonModule,
        MatDialogModule,
        LoadingIndicatorComponent,
        AppStringComponent,
        MatSelectModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        SponsorBannerComponent,
        MatInputModule
    ],
    animations: [
        trigger('detailExpand', [
            state('collapsed,void', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
        ])
    ],
    templateUrl: './golf-scorecard.component.html',
    styleUrl: './golf-scorecard.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GolfScorecardComponent implements OnInit, OnDestroy {
    @ViewChild('scrollSection', { read: ElementRef })
    private scrollSectionRef?: ElementRef<HTMLElement>;
    // prevents rapid spam scrolling if many deltas arrive close together
    private scrollCooldownUntil = 0;
    private readonly FLASH_MS = 500;

    public golfTour!: GolfTournament;
    public dataSource: MatTableDataSource<LeaderRow> = new MatTableDataSource<LeaderRow>([]);
    public columnsToDisplay: string[] = ['pos', 'player', 'tot', 'thru'];
    public roundColumns: string[] = [];
    public roundCount = 0;
    public expandedElement: any;
    private display_place_keys: Map<string, string> = new Map([
        ['dq', 'golf.player.dq'],
        ['wd', 'golf.player.wd'],
        ['cut', 'golf.player.cut'],
        ['even', 'golf.par.even']
    ]);
    public playerFilter: FormControl<PlayerFilterValue> = new FormControl<PlayerFilterValue>('all', { nonNullable: true });
    public playerSearch: FormControl = new FormControl('');
    public fullData: LeaderRow[] = [];
    public playerFilterOptions: PlayerFilterOption[] = this.buildPlayerFilterOptions(true);
    public lastUpdatedDate: Date = new Date();
    private subs = new Subscription();

    private hasLeaderboardData = false;
    private expandedSet = new Set<string>();
    private readonly FINISHED_HOLE = 'F';
    private readonly FULL_ROUND = '18';
    public isLoading = true;
    public hasLoaded = false;
    public showStrokes = false;
    public eventFinished = false;
    public currentRound: number | null = null;
    public searchPlaceholder: string = this.appStringsService.getAppString('golf.leaderboard.search.placeholder');
    public merchandiseButtonLabel: string = this.appStringsService.getAppString('golf.player.merchandise.title');
    private pendingDelta = false;
    private lastRowSignature = new Map<string, string>();
    private flashingRows = new Set<string>();
    private flashTimers = new Map<string, ReturnType<typeof setTimeout>>();
    public scorecardTabsByPlayerId = new Map<string, 'scorecard' | 'stats'>();
    public activeDataSource: MatTableDataSource<LeaderRow> = new MatTableDataSource<LeaderRow>([]);
    public cutDataSource: MatTableDataSource<LeaderRow> = new MatTableDataSource<LeaderRow>([]);
    public selectedRoundByPlayerId = new Map<string, number>();

    private readonly STAT_ORDER: GolfStatKey[] = ['ace', 'double_eagle', 'eagle', 'birdie', 'par', 'bogey', 'double_bogey', 'triple_bogey', 'other'];

    private readonly STAT_LABEL_KEY: Record<GolfStatKey, string> = {
        ace: 'golfstats.ace',
        double_eagle: 'golfstats.eagle.double',
        eagle: 'golfstats.eagle',
        birdie: 'golfstats.birdie',
        par: 'golfstats.par',
        bogey: 'golfstats.bogey',
        double_bogey: 'golfstats.bogey.double',
        triple_bogey: 'golfstats.bogey.triple',
        other: 'golfstats.other'
    };

    constructor(
        private route: ActivatedRoute,
        private appStringsService: AppStringsService,
        private followPlayerAPI: FollowedPlayerService,
        private dialog: MatDialog,
        private analytics: AnalyticsService,
        private platform: Platform,
        private entryState: ScheduledEntryStateService,
        private ssr: SsrlocalstoreService,
        private cdr: ChangeDetectorRef
    ) {}

    private appString(key: string): string {
        return this.appStringsService.cache.get(key) ?? key;
    }

    async followPlayer(player_id: string) {
        await this.followPlayerAPI.followPlayer(player_id);
    }

    async ngOnInit(): Promise<void> {
        const teamId =
            this.route.snapshot.parent?.paramMap.get('team_id') || this.route.snapshot.paramMap.get('team_id') || this.route.parent?.parent?.snapshot.paramMap.get('team_id');

        if (!teamId) {
            console.error('[Scorecard] team_id not found');
            this.isLoading = false;
            this.hasLoaded = true;
            return;
        }

        this.isLoading = true;

        this.subs.add(
            this.entryState.deltaTick.subscribe((tick) => {
                // tick=0 is default / reset; ignore
                if (tick > 0) {
                    this.pendingDelta = true;
                }
            })
        );

        this.subs.add(this.playerFilter.valueChanges.subscribe(() => this.applyFilters()));
        this.subs.add(this.playerSearch.valueChanges.subscribe(() => this.applyFilters()));

        this.subs.add(
            this.entryState.tournament.pipe(filter((t): t is GolfTournament => !!t)).subscribe((tournament) => {
                console.log(tournament);
                this.golfTour = tournament;
                this.lastUpdatedDate = new Date();

                const rows = this.buildLeaderboard();
                this.fullData = rows;
                // Keep selectedRound map in sync with current leaderboard players
                const ids = new Set(rows.map((r) => r.player_id));
                Array.from(this.selectedRoundByPlayerId.keys()).forEach((id) => {
                    if (!ids.has(id)) this.selectedRoundByPlayerId.delete(id);
                });
                this.hasLeaderboardData = rows.length > 0;

                if (this.pendingDelta) {
                    this.flashChangedRows(rows);
                    this.pendingDelta = false;
                } else {
                    this.refreshSignatures(rows);
                }

                this.syncPlayerFilterOptions();
                this.applyFilters();

                this.isLoading = false;
                this.hasLoaded = true;

                this.cdr.markForCheck();
            })
        );

        await this.entryState.init(teamId);
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
        this.flashTimers.forEach((t) => clearTimeout(t));
        this.flashTimers.clear();
        this.flashingRows.clear();
    }

    openPlayer(golf_player: GolfPlayer | Player): void {
        this.dialog.open(PlayerDialogComponent, {
            data: golf_player,
            width: '100%',
            height: '90vh',
            maxWidth: '750px',
            panelClass: 'full-screen-dialog'
        });
    }

    openPlayerGear(player: GolfPlayer, click?: Event): void {
        click?.stopPropagation();

        const merchandiseUrl = player?.merchandise_url;
        if (!merchandiseUrl) return;

        const payload = {
            name: 'player.merchandise.link.view',
            screen_name: 'Golf Scorecard',
            parameters: {
                golf_player_id: player.id,
                url: merchandiseUrl
            }
        };

        this.analytics.postEvent('button_press', payload);

        if (this.ssr.isClientSide) {
            window.open(merchandiseUrl);
        }
    }

    isFlashing(row: LeaderRow): boolean {
        return this.flashingRows.has(row.player_id);
    }

    private rowSignature(row: LeaderRow): string {
        const rounds = row.roundVals?.join('|') ?? '';
        const strokes = (row.totalStrokes ?? '').toString();

        const totalStats = JSON.stringify(row.source?.stats ?? null);

        const roundStats = Array.from({ length: this.roundCount }, (_, i) => {
            const r = i + 1;
            return JSON.stringify(row.source?.[`round_${r}`]?.stats ?? null);
        }).join('|');

        const roundCourseIds = Array.from({ length: this.roundCount }, (_, i) => {
            const r = i + 1;
            return String(row.source?.[`round_${r}`]?.course_id ?? '');
        }).join('|');

        const roundVenues = Array.from({ length: this.roundCount }, (_, i) => {
            const r = i + 1;
            return String(row.source?.[`round_${r}`]?.enet_venue_id ?? '');
        }).join('|');

        return [row.pos, row.placeNum ?? '', row.tot, row.thru, strokes, rounds, totalStats, roundStats, roundCourseIds, roundVenues].join('::');
    }

    // refresh signature map (used for initial + non-delta updates)
    private refreshSignatures(rows: LeaderRow[]): void {
        const next = new Map<string, string>();
        rows.forEach((r) => next.set(r.player_id, this.rowSignature(r)));
        this.lastRowSignature = next;
    }

    // diff signatures and flash changed or newly introduced players on delta updates
    private flashChangedRows(rows: LeaderRow[]): void {
        const next = new Map<string, string>();
        const changed: string[] = [];
        const hadBaseline = this.lastRowSignature.size > 0;

        rows.forEach((r) => {
            const sig = this.rowSignature(r);
            next.set(r.player_id, sig);

            const prev = this.lastRowSignature.get(r.player_id);
            if ((prev !== undefined && prev !== sig) || (prev === undefined && hadBaseline)) {
                changed.push(r.player_id);
            }
        });

        this.lastRowSignature = next;

        changed.forEach((id) => this.startFlash(id));

        // TEST ONLY
        // auto-scroll to the first changed row (delta only)
        // if (changed.length) {
        //   // wait 1-2 frames so mat-table rows exist in the DOM
        //   requestAnimationFrame(() => {
        //     requestAnimationFrame(() => this.scrollToPlayerRow(changed[0]));
        //   });
        // }

        if (changed.length) this.cdr.markForCheck();
    }

    private startFlash(playerId: string): void {
        if (this.flashingRows.has(playerId)) {
            // if update happens while animating, do NOT start animation over again
            return;
        }

        this.flashingRows.add(playerId);

        // Total duration: 400ms fade-in + 100ms fade-out = 500ms
        const timer = setTimeout(() => {
            this.flashingRows.delete(playerId);
            this.flashTimers.delete(playerId);

            // OnPush: timer updates need a markForCheck
            this.cdr.markForCheck();
        }, this.FLASH_MS); // 500

        this.flashTimers.set(playerId, timer);
    }

    private comparePairId(a: LeaderRow, b: LeaderRow): number {
        const aId = a.pairId;
        const bId = b.pairId;

        if (aId === null || aId === undefined) return bId === null || bId === undefined ? 0 : 1;
        if (bId === null || bId === undefined) return -1;

        const aNum = typeof aId === 'number' ? aId : /^\d+$/.test(String(aId)) ? Number(aId) : null;
        const bNum = typeof bId === 'number' ? bId : /^\d+$/.test(String(bId)) ? Number(bId) : null;

        if (aNum !== null && bNum !== null) return aNum - bNum;

        return String(aId).localeCompare(String(bId));
    }

    isFollowing(row: LeaderRow): boolean {
        return this.followPlayerAPI.followed_players.value.includes(row.player_id);
    }

    pos(row: GolfScoreCardModel): string {
        return row.dq
            ? this.appString(this.display_place_keys.get('dq') as string)
            : row.wd
              ? this.appString(this.display_place_keys.get('wd') as string)
              : !row.made_cut
                ? this.appString(this.display_place_keys.get('cut') as string)
                : (row.display_place ?? '-');
    }

    private getRound(sc: any, idx: number): RoundScore {
        const raw = sc[`round_${idx}`];
        if (!raw) return { strokes: null, thru: null };

        let thruVal: string | null = null;
        if (raw.thru === this.FINISHED_HOLE) thruVal = this.FULL_ROUND;
        else if (raw.thru !== undefined && raw.thru !== null) thruVal = raw.thru;

        return { strokes: raw.strokes ?? null, thru: thruVal };
    }

    private extractTeeInfo(sc: any, totalRounds: number): [string | null, number | null] {
        for (let r = 1; r <= totalRounds; r++) {
            const rnd = sc[`round_${r}`];
            if (!rnd) continue;
            const tt = rnd.tee_time ?? null;
            const sh = rnd.starting_hole ?? null;
            if (tt !== null || sh !== null) return [tt, sh];
        }
        return [null, null];
    }

    private isEventFinished(event: GolfEvent): boolean {
        return event.status?.toLowerCase() === 'finished';
    }

    // Find the round that any player is still playing (1‑4), else null
    private detectCurrentRound(event: GolfEvent, total: number): number | null {
        if (!event.scores?.length) return null;
        if (this.isEventFinished(event)) return null;

        for (let r = total; r >= 1; r--) {
            const live = (event.scores as GolfScoreCardModel[]).some((sc: GolfScoreCardModel) => sc[`round_${r}`] && sc[`round_${r}`].thru !== this.FINISHED_HOLE);

            if (live) return r;
        }

        return null;
    }

    private hasToParData(event: GolfEvent): boolean {
        if (!event?.scores?.length) return false;

        return event.scores.some((sc) => {
            for (let r = 1; r <= (event.number_of_rounds ?? 4); r++) {
                const rd = sc[`round_${r}`];
                if (rd && typeof rd.par === 'number') return true;
            }
            return false;
        });
    }

    private buildPlayerFilterOptions(includeLive: boolean): PlayerFilterOption[] {
        const options: PlayerFilterOption[] = [
            { label: this.appStringsService.getAppString('golf.leaderboard.header.all'), value: 'all' },
            { label: this.appStringsService.getAppString('golf.leaderboard.header.live'), value: 'live' },
            { label: this.appStringsService.getAppString('golf.leaderboard.header.followed'), value: 'followed' }
        ];

        return includeLive ? options : options.filter((option) => option.value !== 'live');
    }

    private canShowLiveFilterOption(event?: GolfEvent | null): boolean {
        return !!event && this.golfTour?.hole_by_hole_scoring === true && !this.isEventFinished(event);
    }

    private syncPlayerFilterOptions(): void {
        const strokePlayEvent = this.golfTour?.events?.find((event) => event.type === 'stroke_play');
        const canShowLive = this.canShowLiveFilterOption(strokePlayEvent);

        this.playerFilterOptions = this.buildPlayerFilterOptions(canShowLive);

        if (!canShowLive && this.playerFilter.value === 'live') {
            this.playerFilter.setValue('all', { emitEvent: false });
        }
    }

    private buildLeaderboard(): LeaderRow[] {
        const rows: LeaderRow[] = [];
        let headerRounds: any[] = [];
        let isEmpty = true;

        if (!this.golfTour?.events?.length) return [];
        const event = this.golfTour.events[0] ?? [];
        if (!event || !event.scores?.length) return [];

        const totalRounds: number = event.number_of_rounds ?? 4;

        this.roundCount = totalRounds;
        this.roundColumns = Array.from({ length: totalRounds }, (_, i) => `R${i + 1}`);

        const hbhFlag = this.golfTour?.hole_by_hole_scoring;
        const supportsToPar = typeof hbhFlag === 'boolean' ? hbhFlag : this.hasToParData(event);
        const supportsHBH = !!this.golfTour?.hole_by_hole_scoring;

        headerRounds = Array.from({ length: totalRounds }, (_, i) => i + 1);

        const isFinished = this.isEventFinished(event);
        this.eventFinished = isFinished;

        // Prefer server hint for stroke play; fallback to legacy detection if absent
        const old = (event.type === 'stroke_play' ? this.mostRecentRoundIndex(event) : null) ?? this.detectCurrentRound(event, totalRounds);

        const currentRound =
            !event.most_recently_scored_round || Number.isNaN(event.most_recently_scored_round?.split('_')[1]) ? null : Number(event.most_recently_scored_round?.split('_')[1]);

        this.currentRound = isFinished ? null : currentRound;

        this.showStrokes = isFinished;

        const showThruColumn = supportsHBH && !this.showStrokes;

        this.columnsToDisplay = ['pos', 'player', 'tot', ...(this.showStrokes ? ['strokes'] : showThruColumn ? ['thru'] : []), ...this.roundColumns];

        this.columnsToDisplay.push('expand');

        event.scores.forEach((sc) => {
            const dq = !!sc.dq;
            const wd = !!sc.wd;
            const cut = !sc.made_cut && !dq && !wd;
            const nonPlaying: LeaderRow['nonPlaying'] = dq ? 'dq' : wd ? 'wd' : cut ? 'cut' : null;

            const rawPlace = sc.place;

            const hasNumericPlace = rawPlace !== null && rawPlace !== undefined && rawPlace !== '' && !isNaN(Number(rawPlace));

            const placeNum: number | null = dq || wd || !hasNumericPlace ? null : Number(rawPlace);

            // const pos: string = dq ? this.str(this.DQ_KEY) : wd ? this.str(this.WD_KEY) : cut ? this.str(this.CUT_KEY) : (sc.display_place ?? '-');
            const pos: string = this.pos(sc);

            // --- player name ---
            const f = sc.player?.first_name ?? '';
            const l = sc.player?.last_name ?? '';
            const playerName = `${f} ${l}`.trim();

            // --- total (par) ---
            const parVal: number | null | undefined = sc.par;
            let tot: string;
            if (parVal === null || parVal === undefined) {
                tot = '-';
            } else if (parVal === 0) {
                tot = this.appString(this.display_place_keys.get('even') as string);
            } else if (parVal > 0) {
                tot = `+${parVal}`;
            } else {
                tot = String(parVal);
            }

            if (wd) tot = '-';

            // --- round strokes ---
            const roundVals: (number | string)[] = [];
            const rounds: RoundScore[] = [];

            for (let r = 1; r <= totalRounds; r++) {
                const rd: GolfScoreCardModel = sc[`round_${r}`];
                const rs = this.getRound(sc, r);
                rounds.push(rs);

                let display: number | string = '-';

                if (rd) {
                    const hasPar = typeof rd.par === 'number';

                    /**
                     * Only show to-par for the CURRENT round if:
                     *  - tournament supports HBH (we have per-hole 'par' numbers),
                     *  - this is the detected current round for the event,
                     *  - the event itself is not finished,
                     *  - AND the golfer is actively playing (sc.is_playing === true).
                     * If the golfer finished their round (is_playing === false),
                     * we show STROKES instead.
                     */
                    const canShowToPar = supportsToPar && currentRound === r && !isFinished && hasPar && sc.is_playing;

                    if (canShowToPar) {
                        const par = rd.par!;
                        display = par === 0 ? 'E' : par > 0 ? `+${par}` : String(par);
                    } else {
                        // Tournament finished, past rounds, or no HBH → just show strokes
                        display = rd.strokes ?? '-';
                    }
                }
                roundVals.push(display);
            }

            // Total strokes (for finished events)
            // Sum all available strokes across rounds. If there are no stroke values at all,
            // or the sum is 0, we display '-' (store as null).
            let strokeSum = 0;
            let strokeCount = 0;

            for (let r = 1; r <= totalRounds; r++) {
                const rd = sc[`round_${r}`];
                if (rd && typeof rd.strokes === 'number') {
                    strokeSum += rd.strokes;
                    strokeCount++;
                }
            }

            const totalStrokes: number | null = strokeCount === 0 || strokeSum === 0 ? null : strokeSum;

            // --- thru ---
            let thru = '-';
            if (!(dq || wd || cut)) {
                for (let i = totalRounds - 1; i >= 0; i--) {
                    if (rounds[i].strokes !== null) {
                        const t = rounds[i].thru;
                        thru = t === this.FULL_ROUND ? this.FINISHED_HOLE : (t ?? '-');
                        break;
                    }
                }
            }

            // For unfinished events, the THRU column should reflect the CURRENT round only.
            // If the golfer has not started that round yet, show tee time when available; otherwise "-".
            // This avoids carrying forward "F" from a completed prior round when ENet omits tee_time.
            if (event.type === 'stroke_play' && !(dq || wd || cut)) {
                const rd = sc[`round_${currentRound ?? 1}`];
                const currentThru = rd?.thru ?? null;
                const tee = rd?.tee_time ?? null;
                const startingHole = rd?.starting_hole ?? null;
                const currentRoundHasProgress = (rd?.strokes !== null && rd?.strokes !== undefined) || (currentThru !== null && currentThru !== undefined);

                if (!isFinished && !currentRoundHasProgress) {
                    thru = '-';

                    if (tee) {
                        let displayTee = formatLocalShortTimeFromCET(tee);

                        // If player starts on back nine (holes 10–18), append "*" to tee time
                        if (startingHole !== null && startingHole >= 10 && startingHole <= 18) {
                            displayTee = `${displayTee}*`;
                        }

                        thru = displayTee;
                    }
                } else if ((currentThru === null || currentThru === undefined) && tee) {
                    let displayTee = formatLocalShortTimeFromCET(tee);

                    // If player starts on back nine (holes 10–18), append "*" to tee time
                    if (startingHole !== null && startingHole >= 10 && startingHole <= 18) {
                        displayTee = `${displayTee}*`;
                    }

                    thru = displayTee;
                }
            }

            const [teeTime, startingHole] = this.extractTeeInfo(sc, totalRounds);

            rows.push({
                pos,
                placeNum,
                playerName,
                tot,
                thru,
                roundVals,
                teeTime,
                startingHole,
                player_id: sc.player.id,
                display_place: sc.display_place,
                source: sc,
                totalStrokes,
                pairId: sc.pair_id ?? null,
                sortOrder: typeof sc.sort_order === 'number' ? sc.sort_order : null,
                nonPlaying
            });
        });

        const canUseServerSort = event.type === 'stroke_play' && rows.some((r) => typeof r.sortOrder === 'number');

        if (canUseServerSort) {
            rows.sort((a, b) => {
                const aSo = typeof a.sortOrder === 'number' ? a.sortOrder : Number.MAX_SAFE_INTEGER;
                const bSo = typeof b.sortOrder === 'number' ? b.sortOrder : Number.MAX_SAFE_INTEGER;

                const soCmp = aSo - bSo;
                if (soCmp !== 0) return soCmp;

                const pairCmp = this.comparePairId(a, b);
                if (pairCmp !== 0) return pairCmp;

                return a.playerName.localeCompare(b.playerName);
            });
        } else {
            rows.sort((a, b) => {
                const bucket = (r: LeaderRow): number => {
                    if (r.placeNum !== null) return 0; // ranked
                    if (r.pos?.trim() && r.pos !== '-') return 1; // CUT/WD/DQ
                    return 2; // no place
                };

                const ba = bucket(a);
                const bb = bucket(b);
                if (ba !== bb) return ba - bb;

                switch (ba) {
                    case 0: {
                        const placeCmp = a.placeNum! - b.placeNum!;
                        if (placeCmp !== 0) return placeCmp;

                        const pairCmp = this.comparePairId(a, b);
                        if (pairCmp !== 0) return pairCmp;

                        return a.playerName.localeCompare(b.playerName);
                    }

                    case 1: {
                        const posCmp = a.pos.localeCompare(b.pos);
                        if (posCmp !== 0) return posCmp;

                        const pairCmp = this.comparePairId(a, b);
                        if (pairCmp !== 0) return pairCmp;

                        return a.playerName.localeCompare(b.playerName);
                    }

                    case 2: {
                        // no place
                        if (a.teeTime && b.teeTime) {
                            const tCmp = a.teeTime.localeCompare(b.teeTime);
                            if (tCmp !== 0) return tCmp;
                        } else if (a.teeTime || b.teeTime) {
                            return a.teeTime ? -1 : 1; // row WITH tee-time first
                        }
                        const sA = a.startingHole ?? Number.MAX_SAFE_INTEGER;
                        const sB = b.startingHole ?? Number.MAX_SAFE_INTEGER;
                        const holeCmp = sA - sB;
                        if (holeCmp !== 0) return holeCmp;

                        const pairCmp = this.comparePairId(a, b);
                        if (pairCmp !== 0) return pairCmp;

                        return a.playerName.localeCompare(b.playerName);
                    }
                }
                return 0;
            });
        }

        rows.forEach((r, idx) => (r.rowIndex = idx));

        isEmpty = rows.length === 0;

        return rows;
    }

    handleFollow(player_id: string) {
        const loggedIn = this.ssr.isLoggedIn();
        if (!loggedIn) {
            this.dialog.open(InfoDialogComponent, {
                data: {
                    notice_type: 'login'
                },
                height: '136px',
                width: '336px'
            });
            return;
        }
        this.updateFollowList(player_id);
    }

    async updateFollowList(player_id: string) {
        const current = this.followPlayerAPI.followed_players.value;

        if (current.includes(player_id)) {
            this.followPlayerAPI.unfollowPlayer(player_id);
        } else if (!current.includes(player_id)) {
            this.followPlayerAPI.followPlayer(player_id);
        }

        this.applyFilters();
    }

    onToggleExpand(element: LeaderRow): void {
        const wasExpanded = this.expandedElement === element;

        this.expandedElement = wasExpanded ? null : element;

        if (!wasExpanded) {
            this.onScoreCardExpanded(element);
        }
    }

    async onScoreCardExpanded(element: LeaderRow): Promise<void> {
        const payload = {
            screen_name: 'Golf Scorecard',
            parameters: {
                playerId: element.player_id
            }
        };
        await this.analytics.postEvent('custom_event', payload);
    }

    isExpanded(row: LeaderRow): boolean {
        return this.expandedSet.has(row.player_id);
    }

    toggleExpand(row: LeaderRow): void {
        if (this.isExpanded(row)) {
            this.expandedSet.delete(row.player_id);
        } else {
            this.expandedSet.add(row.player_id);
            this.onScoreCardExpanded(row);
        }
    }

    private mostRecentRoundIndex(event: GolfEvent): number | null {
        const key = event.most_recently_scored_round;
        if (!key || key === 'round_final') return null;
        const map: Record<string, number> = { round_1: 1, round_2: 2, round_3: 3, round_4: 4 };
        return map[key] ?? null;
    }

    private applyFilters(): void {
        if (!this.hasLeaderboardData) {
            this.activeDataSource = new MatTableDataSource<LeaderRow>([]);
            this.cutDataSource = new MatTableDataSource<LeaderRow>([]);
            this.dataSource = new MatTableDataSource<LeaderRow>([]);
            this.cdr.markForCheck();
            return;
        }

        const filterState = this.playerFilter.value ?? 'all';
        const term = (this.playerSearch.value ?? '').toString().trim().toLowerCase();

        let rows = [...this.fullData];

        console.log(this.fullData);

        if (filterState === 'live') {
            rows = rows.filter((r) => !!r.source?.is_playing);
        }

        if (filterState === 'followed') {
            const ids = this.followPlayerAPI.followed_players.value;
            rows = rows.filter((r) => ids.includes(r.player_id));
        }

        if (term) {
            rows = rows.filter((r) => {
                const first = r.source?.player?.first_name ?? '';
                const last = r.source?.player?.last_name ?? '';
                const fullName = `${first} ${last}`.toLowerCase();
                return fullName.includes(term);
            });
        }

        // Split into active vs non-playing (CUT/WD/DQ)
        const activeRows = rows.filter((r) => !r.nonPlaying);
        const cutRows = rows.filter((r) => !!r.nonPlaying);

        console.log('active', activeRows);

        this.activeDataSource = new MatTableDataSource<LeaderRow>(activeRows);
        this.cutDataSource = new MatTableDataSource<LeaderRow>(cutRows);

        this.dataSource = new MatTableDataSource<LeaderRow>(rows);

        this.cdr.markForCheck();
    }

    private normalizeStatsMap(maybe: any): GolfStatsMap | null {
        if (!maybe || typeof maybe !== 'object') return null;
        return maybe as GolfStatsMap;
    }

    private hasAnyStatsValue(map: GolfStatsMap | null): boolean {
        if (!map) return false;
        // keys may not exist; values can be null
        return Object.values(map).some((v) => typeof v === 'number');
    }

    hasStats(sc?: any): boolean {
        if (!sc) return false;

        // total stats exist on score object: sc.stats
        const total = this.normalizeStatsMap(sc.stats);
        if (this.hasAnyStatsValue(total)) return true;

        // round stats exist on each round: sc.round_X.stats
        for (let r = 1; r <= (this.roundCount || 4); r++) {
            const rs = this.normalizeStatsMap(sc?.[`round_${r}`]?.stats);
            if (this.hasAnyStatsValue(rs)) return true;
        }

        return false;
    }

    getSelectedTab(row: LeaderRow): 'scorecard' | 'stats' {
        return this.scorecardTabsByPlayerId.get(row.player_id) ?? 'scorecard';
    }

    async setSelectedTab(row: LeaderRow, tab: 'scorecard' | 'stats', ev?: MouseEvent): Promise<void> {
        ev?.stopPropagation();

        this.scorecardTabsByPlayerId.set(row.player_id, tab);

        if (tab === 'stats') {
            await this.analytics.postEvent('custom_event', {
                screen_name: 'Golf Scorecard - Stats',
                parameters: { player_id: row.player_id }
            });
        }

        this.cdr.markForCheck();
    }

    getStatsColumns(): string[] {
        // Stat, R1..Rmax, Total
        const rounds = Array.from({ length: this.roundCount }, (_, i) => `R${i + 1}`);
        return ['stat', ...rounds, 'total'];
    }

    getStatsRows(sc: any): Array<{
        key: GolfStatKey;
        labelKey: string;
        roundVals: (number | null)[];
        total: number | null;
    }> {
        const totalMap = this.normalizeStatsMap(sc?.stats);
        const byRound: GolfStatsMap[] = [];

        for (let r = 1; r <= this.roundCount; r++) {
            byRound.push(this.normalizeStatsMap(sc?.[`round_${r}`]?.stats) ?? {});
        }

        return this.STAT_ORDER.map((k) => {
            const roundVals = byRound.map((m) => (k in m ? (m[k] ?? null) : null));
            const total = totalMap && k in totalMap ? (totalMap[k] ?? null) : null;

            return {
                key: k,
                labelKey: this.STAT_LABEL_KEY[k],
                roundVals,
                total
            };
        });
    }

    displayStat(v: number | null): string {
        return typeof v === 'number' ? String(v) : '-';
    }

    hasScorecardData(sc?: any): boolean {
        if (!sc) return false;

        for (let r = 1; r <= (this.roundCount || 4); r++) {
            const round = sc?.[`round_${r}`];

            if (
                round &&
                ((round.strokes !== null && round.strokes !== undefined) ||
                    (round.thru !== null && round.thru !== undefined) ||
                    (Array.isArray(round.holes) && round.holes.length > 0))
            ) {
                return true;
            }
        }

        return false;
    }

    private isRowVisibleInContainer(rowEl: HTMLElement, containerEl: HTMLElement): boolean {
        const rowRect = rowEl.getBoundingClientRect();
        const contRect = containerEl.getBoundingClientRect();

        // fully visible
        return rowRect.top >= contRect.top && rowRect.bottom <= contRect.bottom;
    }

    private scrollToPlayerRow(playerId: string): void {
        const now = Date.now();
        if (now < this.scrollCooldownUntil) return;

        const containerEl = this.scrollSectionRef?.nativeElement;
        if (!containerEl) return;

        const rowEl = containerEl.querySelector<HTMLElement>(`[data-player-id="${playerId}"]`);
        if (!rowEl) return;

        // Don’t scroll if already visible
        if (this.isRowVisibleInContainer(rowEl, containerEl)) return;

        // smooth scroll to center so it's obvious
        rowEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });

        // small cooldown so repeated deltas don’t yank the screen constantly
        this.scrollCooldownUntil = now + 1200;
    }

    // Used by the template to restore the last selected round for a player.
    // Fallback to currentRound if available; else 1
    getSelectedRound(row: LeaderRow): number {
        return this.selectedRoundByPlayerId.get(row.player_id) ?? this.currentRound ?? 1;
    }

    // Called when ScoreCardDetails notifies a round change.
    setSelectedRound(row: LeaderRow, round: number): void {
        if (!round || Number.isNaN(round)) return;
        this.selectedRoundByPlayerId.set(row.player_id, round);
        this.cdr.markForCheck();
    }

    get showNoLiveEmpty(): boolean {
        return this.playerFilterOptions.some((option) => option.value === 'live') && this.showPicker && this.playerFilter.value === 'live' && this.dataSource.data.length === 0;
    }

    clearSearch(event: MouseEvent): void {
        event.stopPropagation();
        this.playerSearch.setValue('');
    }

    get showPicker(): boolean {
        return this.hasLoaded && !this.isLoading && this.hasLeaderboardData;
    }

    get showGenericEmpty(): boolean {
        return this.hasLoaded && !this.isLoading && !this.hasLeaderboardData;
    }

    get showNoFollowedEmpty(): boolean {
        return this.showPicker && this.playerFilter.value === 'followed' && this.dataSource.data.length === 0;
    }

    get countryFlagTop(): string {
        return this.platform.SAFARI ? '0.5rem' : '0.6rem';
    }

    get showAllFilteredEmpty(): boolean {
        return this.showPicker && this.playerFilter.value === 'all' && this.dataSource.data.length === 0;
    }

    get hasCutPlayers(): boolean {
        return (this.cutDataSource?.data?.length ?? 0) > 0;
    }
}
